import React from 'react';
import cn from 'classnames';
import {
  noteOrReminderAction,
  Interaction as TimelineInteractionModel,
  TimelineItem,
} from '@models/Timeline';
import importAllFiles from '@support/importAllFiles';
import styles from './Interaction.module.scss';
import { useTheme } from '@support/hooks/use-theme';
import Contact from '@models/Contact';
import { useWorkspaceContext } from '@support/hooks/use-workspace-context';
import { NoteBadge, ReminderDateDisplay } from '@components/TimelineNote/Header';
import Occasion from '@support/Occasion';
import { useSelector } from 'react-redux';
import { selectNoteType } from '@reducers/data/notes';
import { selectNote } from '@actions/notes';

const icons = importAllFiles(import.meta.glob('/public/img/contact/icons/source/*.svg'));

const InteractionBadge = ({
  timelineItem,
  editable = true,
}: {
  timelineItem: TimelineItem;
  editable?: boolean;
}) => {
  const [theme] = useTheme();
  const iconKey = timelineItem.interaction?.iconKey;
  const storedNote = useSelector(selectNote(timelineItem.noteID));
  const noteType = useSelector(selectNoteType(timelineItem.noteID));

  if (!iconKey && ['note', 'reminder'].includes(timelineItem.type)) {
    return (
      <NoteBadge
        reminder={storedNote.reminder}
        user={timelineItem.user}
        className={styles.badge}
        noteType={noteType}
        editable={editable}
      />
    );
  }

  if (!iconKey) {
    return <span />;
  }

  return <img className={styles.badge} src={icons[`${iconKey}_${theme}`] || icons[iconKey]} />;
};

const InteractionLink = ({ interaction }: { interaction: TimelineInteractionModel }) => {
  const Component = interaction.link ? 'a' : 'span';

  const props: any = {
    href: interaction.link,
  };

  if (interaction.link?.startsWith('http')) {
    props.target = '_blank';
  }

  return !interaction.title ? null : <Component {...props}>{interaction.title.trim()}</Component>;
};

const InteractionDescription = ({
  contact,
  timelineItem,
  className,
}: {
  contact: Contact;
  timelineItem: TimelineItem;
  className?: string;
}) => {
  const { isMultiPlayer } = useWorkspaceContext();
  const contactName = contact.firstName || contact.displayName;
  const storedNote = useSelector(selectNote(String(timelineItem.noteID)));
  const noteLabel = storedNote?.type?.label && (
    <span className={styles.noteLabel}>{storedNote.type.label}</span>
  );
  const actionTaken = ['note', 'reminder'].includes(timelineItem.type)
    ? noteOrReminderAction(storedNote.reminder)
    : timelineItem.interaction.actionTaken;

  let userName = <b>{timelineItem?.user?.name}</b>;
  if (!isMultiPlayer || timelineItem?.user?.is_self) {
    userName = <>You</>;
  }

  let desc = (
    <>
      {noteLabel}
      {userName} {actionTaken} {!timelineItem.noteID ? contactName : ''}{' '}
    </>
  );

  if (!timelineItem?.user) {
    desc = (
      <>
        {noteLabel}
        {contactName} {actionTaken}{' '}
      </>
    );
  }

  let reminderDate = null;
  if (storedNote?.reminder?.date) {
    reminderDate = new Occasion(storedNote.reminder.date);
  }

  return (
    <div className={cn(styles.interactionDescription, className)}>
      {desc}
      <InteractionLink interaction={timelineItem.interaction} />
      {reminderDate && <ReminderDateDisplay date={reminderDate} />}
    </div>
  );
};

export const SidebarTimelineInteraction = ({
  contact,
  timelineItem,
  isLast,
}: {
  contact: Contact;
  timelineItem: TimelineItem;
  isLast: boolean;
}) => {
  return (
    <div className={cn(styles.container, !isLast && styles.notLast, styles.sidebar)}>
      <InteractionBadge timelineItem={timelineItem} editable={false} />
      <div className={styles.sidebarInteractionMetadata}>
        <InteractionDescription
          contact={contact}
          timelineItem={timelineItem}
          className={styles.sidebar}
        />
        <span className={cn(styles.interactionDate, styles.sidebar)}>
          {timelineItem.interaction?.sidebarDateDisplay}
        </span>
      </div>
    </div>
  );
};

export default function TimelineInteraction({
  contact,
  timelineItem,
  isLast,
}: {
  contact: Contact;
  timelineItem: TimelineItem;
  isLast: boolean;
}) {
  return (
    <div className={cn(styles.container, !isLast && styles.notLast)}>
      <InteractionBadge timelineItem={timelineItem} />
      <InteractionDescription contact={contact} timelineItem={timelineItem} />
      <span className={styles.interactionDate}>{timelineItem.interaction.dateDisplay}</span>
    </div>
  );
}
